import { useIsJobillan, useJobillaOrgId } from '@/hooks'
import { Button, SubtleLoader } from '@/ui'
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react'
import { ArrowDownTrayIcon, ArrowPathIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { axiosInstance, InvoiceListItemData } from '@jobilla/nexus-sdk'
import { ArrowUpRightIcon, ChevronsDownIcon } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

type InvoicePreviewDialogProps = {
	open: boolean
	onClose: () => void
	invoice: InvoiceListItemData
	// Which record to open on: a payment id shows that receipt; null/undefined shows the invoice.
	initialPaymentId?: string | null
}

// A record the modal can preview: the invoice itself, or a receipt for one paid payment.
type PreviewRecord = { type: 'invoice' } | { type: 'receipt'; paymentId: string }

// A4 at 96 CSS dpi: 210mm × 297mm. The iframe renders at A4 width and is scaled
// (zoom) to fit the modal width. Its element height fills the desk, so the desk
// never scrolls; content longer than one A4 page scrolls inside the iframe itself.
const A4_WIDTH_PX = Math.round((210 * 96) / 25.4) // 794
const A4_HEIGHT_PX = Math.round((297 * 96) / 25.4) // 1123
const DESK_PADDING_PX = 48 // matches the p-6 desk padding on both axes

// Injected into the sandboxed preview so it can report its own scroll state — the
// opaque-origin iframe can't be inspected from the parent, but postMessage crosses
// origins. The parent matches on the `source` tag.
const SCROLL_REPORTER = `
<script>
	(function () {
		function report() {
			parent.postMessage({
				source: 'invoice-preview',
				scrollable: document.documentElement.scrollHeight - window.innerHeight > 4,
				scrolled: window.scrollY > 4,
			}, '*')
		}
		window.addEventListener('scroll', report, { passive: true })
		window.addEventListener('load', report)
		window.addEventListener('message', function (event) {
			if (event.data && event.data.source === 'invoice-preview-host' && event.data.action === 'scroll-bottom') {
				window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'smooth' })
			}
		})
		report()
	})()
</script>`

// Matches the lightweight toolbar button used elsewhere in billing settings
// (e.g. the wallet "Top up" trigger).
const toolbarButtonClass =
	'flex items-center gap-1.5 rounded-md border border-grey-200 bg-white px-2.5 py-1 text-xs font-medium text-grey-700 transition-colors hover:border-brand-500 hover:text-brand-700 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-grey-200 disabled:hover:text-grey-700'

const toolbarIconButtonClass =
	'inline-flex size-8 items-center justify-center rounded-md border border-grey-200 bg-white text-grey-500 transition-colors hover:border-brand-500 hover:text-brand-700 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-grey-200 disabled:hover:text-grey-500'

// Bordered variant for the Stripe wordmark + external-link arrow (a wide square button
// would clip the wordmark).
const toolbarLogoButtonClass =
	'inline-flex h-8 items-center gap-1.5 rounded-md border border-grey-200 bg-white px-2.5 transition-colors hover:border-brand-500 cursor-pointer'

// Stripe wordmark (official brand asset), tinted to the Stripe brand purple.
function StripeLogo({ className }: { className?: string }) {
	return (
		<svg viewBox="0 0 60 25" fill="#635BFF" xmlns="http://www.w3.org/2000/svg" className={className}>
			<path
				fillRule="evenodd"
				clipRule="evenodd"
				d="M59.64 14.28h-8.06c.19 1.93 1.6 2.55 3.2 2.55 1.64 0 2.96-.37 4.05-.95v3.32a8.33 8.33 0 0 1-4.56 1.1c-4.01 0-6.83-2.5-6.83-7.48 0-4.19 2.39-7.52 6.3-7.52 3.92 0 5.96 3.28 5.96 7.5 0 .4-.04 1.26-.06 1.48zm-5.92-5.62c-1.03 0-2.17.73-2.17 2.58h4.25c0-1.85-1.07-2.58-2.08-2.58zM40.95 20.3c-1.44 0-2.32-.6-2.9-1.04l-.02 4.63-4.12.87V5.57h3.76l.08 1.02a4.7 4.7 0 0 1 3.23-1.29c2.9 0 5.62 2.6 5.62 7.4 0 5.23-2.7 7.6-5.65 7.6zM40 8.95c-.95 0-1.54.34-1.97.81l.02 6.12c.4.44.98.78 1.95.78 1.52 0 2.54-1.65 2.54-3.87 0-2.15-1.04-3.84-2.54-3.84zM28.24 5.57h4.13v14.44h-4.13V5.57zm0-4.7L32.37 0v3.36l-4.13.88V.88zm-4.32 9.35v9.79H19.8V5.57h3.7l.12 1.22c1-1.77 3.07-1.41 3.62-1.22v3.79c-.52-.17-2.29-.43-3.32.86zm-8.55 4.72c0 2.43 2.6 1.68 3.12 1.46v3.36c-.55.3-1.54.54-2.89.54a4.15 4.15 0 0 1-4.27-4.24l.02-13.17 4.02-.86v3.54h3.14V9.1h-3.14v5.85zm-4.91.7c0 2.97-2.31 4.66-5.73 4.66a11.2 11.2 0 0 1-4.46-.93v-3.93c1.38.75 3.1 1.31 4.46 1.31.92 0 1.53-.24 1.53-1C6.16 13.9 0 14.84 0 10.43 0 7.5 2.24 5.76 5.58 5.76c1.6 0 3.2.25 4.8.9v3.88a10.73 10.73 0 0 0-4.8-1.24c-.87 0-1.42.25-1.42.9 0 1.4 6.16.73 6.16 5.04z"
			/>
		</svg>
	)
}

/**
 * In-app invoice/receipt preview. Cycles through the invoice and a receipt for each
 * settled payment, fetching the Blade-rendered HTML from the
 * `/{organisation}/invoices/{invoice}/preview` and
 * `/{organisation}/invoices/{invoice}/payments/{payment}/preview` endpoints and showing
 * it in an iframe sized to a real A4 sheet.
 */
export function InvoicePreviewDialog(
	{ open, onClose, invoice, initialPaymentId }: InvoicePreviewDialogProps,
) {
	const organisationId = useJobillaOrgId()
	const isJobillan = useIsJobillan()
	const [html, setHtml] = useState<string | null>(null)
	const [isLoading, setIsLoading] = useState(false)
	const [isError, setIsError] = useState(false)
	const [scale, setScale] = useState(1)
	const [frameHeight, setFrameHeight] = useState(A4_HEIGHT_PX)
	const [index, setIndex] = useState(0)
	const [isGenerating, setIsGenerating] = useState(false)
	// Scroll hint: shown while the current record has more below the fold and the user
	// hasn't scrolled it yet. Reset whenever the open record changes.
	const [canScroll, setCanScroll] = useState(false)
	const [hasScrolled, setHasScrolled] = useState(false)

	const deskRef = useRef<HTMLDivElement>(null)
	const iframeRef = useRef<HTMLIFrameElement>(null)

	// The invoice's settled payments (already paid, oldest first) give the receipts to
	// cycle through — taken from the list payload, so no extra request is needed.
	const records = useMemo<PreviewRecord[]>(() => {
		const receipts = invoice.payments.map((payment): PreviewRecord => ({
			type: 'receipt',
			paymentId: payment.id,
		}))

		return [{ type: 'invoice' }, ...receipts]
	}, [invoice.payments])

	const currentRecord: PreviewRecord = records[index] ?? { type: 'invoice' }
	const currentTitle = currentRecord.type === 'invoice'
		? `Invoice ${invoice.number ?? invoice.stripeInvoiceId}`
		: `Receipt ${invoice.payments.find((payment) => payment.id === currentRecord.paymentId)?.receiptNumber ?? ''}`
	const currentUrl = currentRecord.type === 'invoice'
		? `/${organisationId}/invoices/${invoice.id}/preview`
		: `/${organisationId}/invoices/${invoice.id}/payments/${currentRecord.paymentId}/preview`
	const pdfUrl = currentRecord.type === 'invoice'
		? `/${organisationId}/invoices/${invoice.id}/pdf`
		: `/${organisationId}/invoices/${invoice.id}/payments/${currentRecord.paymentId}/pdf`

	const load = useCallback(async () => {
		if (!organisationId) { return }

		setIsLoading(true)
		setIsError(false)

		try {
			const { data } = await axiosInstance.get<string>(
				currentUrl,
				{ responseType: 'text', headers: { Accept: 'text/html' }, transformResponse: (raw) => raw },
			)
			setHtml(data)
		} catch {
			setIsError(true)
		} finally {
			setIsLoading(false)
		}
	}, [organisationId, currentUrl])

	// Renders the current record to a PDF and opens it in a new tab.
	const generatePdf = useCallback(async () => {
		if (!organisationId) { return }

		setIsGenerating(true)
		try {
			const { data } = await axiosInstance.get<Blob>(pdfUrl, { responseType: 'blob' })
			window.open(URL.createObjectURL(data), '_blank', 'noopener,noreferrer')
		} catch {
			setIsError(true)
		} finally {
			setIsGenerating(false)
		}
	}, [organisationId, pdfUrl])

	// Open on the requested record: a payment id positions to that receipt, otherwise
	// the invoice (index 0). Position once per open so manual navigation isn't overridden.
	const positionedRef = useRef(false)
	useEffect(() => {
		if (!open) {
			positionedRef.current = false
			return
		}
		if (positionedRef.current) { return }
		if (!initialPaymentId) {
			setIndex(0)
			positionedRef.current = true
			return
		}
		const target = records.findIndex(
			(record) => record.type === 'receipt' && record.paymentId === initialPaymentId,
		)
		if (target >= 0) {
			setIndex(target)
			positionedRef.current = true
		}
	}, [open, initialPaymentId, records])

	useEffect(() => {
		if (open) { load() }
	}, [open, load])

	// A fresh record starts un-scrolled; the iframe re-reports whether it overflows.
	useEffect(() => {
		setHasScrolled(false)
		setCanScroll(false)
	}, [open, index])

	// The sandboxed preview reports its scroll state to us (see SCROLL_REPORTER).
	useEffect(() => {
		if (!open) { return }
		const onMessage = (event: MessageEvent) => {
			const data = event.data as { source?: string; scrollable?: boolean; scrolled?: boolean }
			if (data?.source !== 'invoice-preview') { return }
			setCanScroll(!!data.scrollable)
			if (data.scrolled) { setHasScrolled(true) }
		}
		window.addEventListener('message', onMessage)
		return () => window.removeEventListener('message', onMessage)
	}, [open])

	// Fit the A4 page to the desk width (never up — keeps it crisp). The iframe's
	// element height is set so that, after zoom, it exactly fills the desk height:
	// the desk stays overflow-hidden and the iframe scrolls its own content.
	const fitPage = useCallback(() => {
		const desk = deskRef.current
		if (!desk) { return }
		const availableWidth = desk.clientWidth - DESK_PADDING_PX
		const availableHeight = desk.clientHeight - DESK_PADDING_PX
		const nextScale = Math.max(0.1, Math.min(1, availableWidth / A4_WIDTH_PX))
		setScale(nextScale)
		setFrameHeight(availableHeight / nextScale)
	}, [])

	useEffect(() => {
		if (!open) { return }
		const desk = deskRef.current
		if (!desk) { return }
		const observer = new ResizeObserver(fitPage)
		observer.observe(desk)
		fitPage()
		return () => observer.disconnect()
	}, [open, html, fitPage])

	const hasMultiple = records.length > 1

	return (
		<AnimatePresence>
			{open && (
				<Dialog static open={open} onClose={onClose} className="relative z-50">
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						className="fixed inset-0 bg-black/30 backdrop-blur-sm"
					/>

					<div className="fixed inset-0 flex items-center justify-center p-4">
						<DialogPanel
							as={motion.div}
							initial={{ opacity: 0, scale: 0.95 }}
							animate={{ opacity: 1, scale: 1 }}
							exit={{ opacity: 0, scale: 0.95 }}
							className="bg-white rounded-lg shadow-xl w-full max-w-4xl h-[90vh] flex flex-col overflow-hidden"
						>
							<div className="flex items-center justify-between gap-4 px-6 py-4 border-b border-grey-100">
								<DialogTitle className="text-lg font-semibold text-gray-900">
									{currentTitle}
								</DialogTitle>
								<div className="flex items-center gap-2">
									{hasMultiple && (
										<div className="flex items-center gap-2">
											<button
												type="button"
												className={toolbarButtonClass}
												onClick={() => setIndex((current) => Math.max(0, current - 1))}
												disabled={index === 0}
											>
												←
											</button>
											<span className="text-xs tabular-nums text-grey-700">
												{index + 1} / {records.length}
											</span>
											<button
												type="button"
												className={toolbarButtonClass}
												onClick={() =>
													setIndex((current) => Math.min(records.length - 1, current + 1))}
												disabled={index >= records.length - 1}
											>
												→
											</button>
										</div>
									)}
									{isJobillan && (
										<button
											type="button"
											className={toolbarLogoButtonClass}
											onClick={() =>
												window.open(
													invoice.stripeDashboardUrl,
													'_blank',
													'noopener,noreferrer',
												)}
											aria-label="Open in Stripe dashboard"
											title="Open in Stripe dashboard"
										>
											<StripeLogo className="h-4 w-auto" />
											<ArrowUpRightIcon className="size-3 text-grey-400" />
										</button>
									)}
									<button
										type="button"
										className={toolbarIconButtonClass}
										onClick={generatePdf}
										disabled={isGenerating}
										aria-label="Download PDF"
										title="Download PDF"
									>
										{isGenerating
											? (
												<span className="size-4 animate-spin rounded-full border-[1.5px] border-grey-300 border-t-brand-600" />
											)
											: <ArrowDownTrayIcon className="size-4" />}
									</button>
									<button
										type="button"
										className={toolbarIconButtonClass}
										onClick={load}
										disabled={isLoading}
										aria-label="Refresh"
										title="Refresh"
									>
										<ArrowPathIcon className={`size-4 ${isLoading ? 'animate-spin' : ''}`} />
									</button>
									<button
										type="button"
										className={toolbarIconButtonClass}
										onClick={onClose}
										aria-label="Close"
										title="Close"
									>
										<XMarkIcon className="size-5" />
									</button>
								</div>
							</div>

							<div
								ref={deskRef}
								className="relative flex flex-1 items-start justify-center overflow-hidden bg-grey-200 p-6"
							>
								{isError
									? (
										<div className="text-center">
											<p className="text-sm text-grey-700">Failed to render the preview.</p>
											<Button type="subtle" size="short" onClick={load} isLoading={isLoading}>
												Retry
											</Button>
										</div>
									)
									: html === null
									? <SubtleLoader delay={0} />
									: (
										<iframe
											ref={iframeRef}
											title={currentRecord.type === 'invoice'
												? 'Invoice PDF preview'
												: 'Receipt PDF preview'}
											srcDoc={html.replace('</body>', `${SCROLL_REPORTER}</body>`)}
											// allow-scripts (without allow-same-origin) lets the Tailwind Play CDN
											// JIT and the injected scroll reporter run, while keeping the iframe a
											// unique opaque origin.
											sandbox="allow-scripts"
											onLoad={fitPage}
											style={{
												width: A4_WIDTH_PX,
												height: frameHeight,
												zoom: scale,
											}}
											className="shrink-0 border border-grey-300 bg-white shadow-lg"
										/>
									)}

								{canScroll && !hasScrolled && (
									<div className="pointer-events-none absolute inset-x-0 bottom-0 flex justify-center pb-4">
										<button
											type="button"
											onClick={() =>
												iframeRef.current?.contentWindow?.postMessage(
													{ source: 'invoice-preview-host', action: 'scroll-bottom' },
													'*',
												)}
											aria-label="Scroll to the bottom"
											className="pointer-events-auto flex size-9 cursor-pointer items-center justify-center rounded-full bg-grey-900/70 text-white shadow-lg transition-colors hover:bg-grey-900/85 motion-safe:animate-bounce"
										>
											<ChevronsDownIcon className="size-5" />
										</button>
									</div>
								)}
							</div>
						</DialogPanel>
					</div>
				</Dialog>
			)}
		</AnimatePresence>
	)
}
